import React, { useState, useEffect } from "react";
import { db } from "../../../firebase";
import {
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import { Button, Dropdown, Badge } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import { useAuth } from "../../../context/AuthContext";
import MaskTransactionItem from "./MaskTransactionItem";
import MaskTransactionModal from "./MaskTransactionModal";
import { notifyTopRight, notifyError } from "../toast/SetToast";
import Toast from "../toast/SetToast";
import swal from "sweetalert";
import fontkit from "@pdf-lib/fontkit";
import { PDFDocument } from "pdf-lib";

const MaskTransactionList = () => {
  const { currentUser } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [textSearch, setTextSearch] = useState("");
  const [searchBy, setSearchBy] = useState("HN");
  const [activityFilter, setActivityFilter] = useState("ทั้งหมด");
  const [statusFilter, setStatusFilter] = useState("ทั้งหมด");
  const [nodeOptions, setNodeOptions] = useState([]);
  const [centerFilter, setCenterFilter] = useState("BD");
  const allowedCenters = ["BD", "LPH"];

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const collectionRef = collectionGroup(db, "maskLogs");
      let queryConstraints = [];

      if (centerFilter) {
        queryConstraints.push(where("center", "==", centerFilter));
      }

      // Search filter
      if (textSearch) {
        if (searchBy === "HN") {
          queryConstraints.push(where("HN", ">=", textSearch));
          queryConstraints.push(where("HN", "<=", textSearch + "\uf8ff"));
        } else if (searchBy === "Name") {
          queryConstraints.push(where("firstname", ">=", textSearch));
          queryConstraints.push(
            where("firstname", "<=", textSearch + "\uf8ff"),
          );
        }
      }

      // Activity filter
      if (activityFilter !== "ทั้งหมด") {
        queryConstraints.push(where("activity", "==", activityFilter));
      }

      // Status filter (isReturned)
      if (statusFilter === "Active") {
        queryConstraints.push(where("isReturned", "==", false));
      } else if (statusFilter === "Returned") {
        queryConstraints.push(where("isReturned", "==", true));
      }

      const transactionQuery =
        queryConstraints.length > 0
          ? query(collectionRef, ...queryConstraints)
          : collectionRef;

      const snapshot = await getDocs(transactionQuery);

      const transactionData = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const formattedDatetime =
          data.datetime && data.datetime.seconds
            ? new Date(
                (data.datetime.seconds + 7 * 60 * 60) * 1000,
              ).toLocaleDateString()
            : "Unknown Date";

        const formattedDueDate =
          data.dueDate && data.dueDate.seconds
            ? new Date(
                (data.dueDate.seconds + 7 * 60 * 60) * 1000,
              ).toLocaleDateString()
            : "";

        return {
          id: docSnap.id,
          parentPath:
            docSnap.ref.parent && docSnap.ref.parent.parent
              ? docSnap.ref.parent.parent.id
              : "",
          ...data,
          datetime: formattedDatetime,
          dueDateFormatted: formattedDueDate,
          datetimeRaw: data.datetime,
          dueDateRaw: data.dueDate,
        };
      });

      // Sort by datetime descending
      transactionData.sort((a, b) => {
        const dateA =
          a.datetimeRaw && a.datetimeRaw.seconds ? a.datetimeRaw.seconds : 0;
        const dateB =
          b.datetimeRaw && b.datetimeRaw.seconds ? b.datetimeRaw.seconds : 0;
        return dateB - dateA;
      });

      // Group transactions by orderId
      const groupedOrders = {};
      transactionData.forEach((item) => {
        const key = item.orderId || item.id; // fallback for items without orderId
        if (!groupedOrders[key]) {
          groupedOrders[key] = {
            ...item, // Use first item's common data (HN, name, date, etc.)
            items: [],
          };
        }
        groupedOrders[key].items.push(item);
      });

      // Convert to array and calculate order-level status
      const groupedTransactions = Object.values(groupedOrders).map((order) => {
        // Order is returned only if ALL items are returned
        const allReturned = order.items.every((item) => item.isReturned);
        // Order is active if ANY item is not returned
        const anyActive = order.items.some(
          (item) =>
            !item.isReturned &&
            (item.activity === "Borrow" || item.activity === "Rent"),
        );
        return {
          ...order,
          isReturned: allReturned,
          hasActiveItems: anyActive,
        };
      });

      // Sort by orderId descending (newest first)
      groupedTransactions.sort((a, b) => {
        const orderA = a.orderId || "";
        const orderB = b.orderId || "";
        return orderB.localeCompare(orderA);
      });

      console.log("Grouped Transactions:", groupedTransactions);
      setTransactions(groupedTransactions);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      notifyError("Error fetching transactions");
    } finally {
      setLoading(false);
    }
  };

  const fetchNodes = async () => {
    try {
      const nodesRef = collection(db, "nodes");
      const snapshot = await getDocs(nodesRef);
      const codes = snapshot.docs
        .map((doc) => doc.data().nodecode)
        .filter(Boolean);
      const filtered = codes.filter((c) => allowedCenters.includes(c));
      setNodeOptions(filtered);
      if (filtered.length > 0 && !filtered.includes(centerFilter)) {
        setCenterFilter(filtered[0]);
      }
    } catch (error) {
      console.error("Error fetching nodes:", error);
    }
  };

  useEffect(() => {
    fetchNodes();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [textSearch, activityFilter, statusFilter, centerFilter]);

  const onSearchChange = (e) => {
    setTimeout(() => {
      setTextSearch(e.target.value);
    }, 800);
  };

  const handleEdit = (transaction) => {
    setSelectedTransaction(transaction);
    setShowModal(true);
  };

  const handleEditSubmit = async (updatedData) => {
    try {
      // Get all items in the order
      const items = selectedTransaction.items || [selectedTransaction];

      // Update note for all items in the order
      for (const item of items) {
        const transactionRef = doc(
          db,
          "maskInventory",
          item.inventoryDocId,
          "maskLogs",
          item.id,
        );

        await updateDoc(transactionRef, {
          note: updatedData.note,
        });
      }

      notifyTopRight("Note updated successfully");
      setShowModal(false);
      fetchTransactions();
    } catch (error) {
      console.error("Error updating transaction:", error);
      notifyError("Error updating transaction");
    }
  };

  const handleDelete = async (transaction) => {
    // Get all items in the order
    const items = transaction.items || [transaction];

    const itemsList = items
      .map((item) => `${item.amount} x ${item.maskName} (${item.size})`)
      .join("\n");

    swal({
      title: "Are you sure?",
      text: `Delete order ${transaction.orderId} with ${items.length} item(s)?\n\n${itemsList}\n\nStock will be restored for non-returned items.`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          // Loop through ALL items in the order
          for (const item of items) {
            const { id, inventoryDocId, activity, amount } = item;

            // Get current inventory data
            const inventoryRef = doc(db, "maskInventory", inventoryDocId);
            const inventorySnap = await getDoc(inventoryRef);

            if (inventorySnap.exists()) {
              const inventoryData = inventorySnap.data();
              let newAvailable = parseInt(inventoryData.available || 0, 10);
              let newBorrowed = parseInt(inventoryData.borrowed || 0, 10);
              let newRented = parseInt(inventoryData.rented || 0, 10);
              const qty = parseInt(amount, 10);

              // Only restore stock if NOT already returned
              if (!item.isReturned) {
                if (activity === "Borrow") {
                  newAvailable += qty;
                  newBorrowed = Math.max(0, newBorrowed - qty);
                } else if (activity === "Rent") {
                  newAvailable += qty;
                  newRented = Math.max(0, newRented - qty);
                }
              }

              // For "Return" activity deletion, reverse the return
              if (activity === "Return") {
                newAvailable = Math.max(0, newAvailable - qty);
                newBorrowed += qty;
              }

              // Update inventory
              await updateDoc(inventoryRef, {
                available: newAvailable,
                borrowed: newBorrowed,
                rented: newRented,
              });
            }

            // Delete this item's transaction document
            const transactionRef = doc(
              db,
              "maskInventory",
              inventoryDocId,
              "maskLogs",
              id,
            );
            await deleteDoc(transactionRef);
          }

          swal(
            "Deleted!",
            `Order with ${items.length} item(s) has been deleted.`,
            "success",
          );
          fetchTransactions();
        } catch (error) {
          console.error("Error deleting transaction:", error);
          swal("Error!", "An error occurred while deleting.", "error");
        }
      }
    });
  };

  const handleReturn = async (order) => {
    // Get all items in the order
    const items = order.items || [order];
    const itemCount = items.filter(
      (item) =>
        !item.isReturned &&
        (item.activity === "Borrow" || item.activity === "Rent"),
    ).length;

    const itemsList = items
      .filter((item) => !item.isReturned)
      .map((item) => `${item.amount} x ${item.maskName} (${item.size})`)
      .join("\n");

    swal({
      title: "Confirm Return",
      text: `Return all ${itemCount} item(s) in order ${order.orderId}?\n\n${itemsList}`,
      icon: "info",
      buttons: true,
    }).then(async (willReturn) => {
      if (willReturn) {
        try {
          // Loop through all items in the order
          for (const item of items) {
            // Skip already returned items
            if (item.isReturned) continue;
            // Skip items that are not Borrow or Rent
            if (item.activity !== "Borrow" && item.activity !== "Rent")
              continue;

            const { id, inventoryDocId, activity, amount } = item;
            const qty = parseInt(amount, 10);

            // Update inventory
            const inventoryRef = doc(db, "maskInventory", inventoryDocId);
            const inventorySnap = await getDoc(inventoryRef);

            if (inventorySnap.exists()) {
              const data = inventorySnap.data();
              let newAvailable = parseInt(data.available || 0, 10) + qty;
              let newBorrowed = parseInt(data.borrowed || 0, 10);
              let newRented = parseInt(data.rented || 0, 10);

              if (activity === "Borrow") {
                newBorrowed = Math.max(0, newBorrowed - qty);
              } else if (activity === "Rent") {
                newRented = Math.max(0, newRented - qty);
              }

              await updateDoc(inventoryRef, {
                available: newAvailable,
                borrowed: newBorrowed,
                rented: newRented,
              });
            }

            // Mark transaction as returned
            const transactionRef = doc(
              db,
              "maskInventory",
              inventoryDocId,
              "maskLogs",
              id,
            );
            await updateDoc(transactionRef, { isReturned: true });
          }

          swal("Success!", "All items have been returned.", "success");
          fetchTransactions();
        } catch (error) {
          console.error("Error returning items:", error);
          swal("Error!", "An error occurred.", "error");
        }
      }
    });
  };

  const handleDownloadPDF = async (transaction) => {
    try {
      // Fetch all transactions with the same orderId
      const orderId = transaction.orderId;
      const orderQuery = query(
        collectionGroup(db, "maskLogs"),
        where("orderId", "==", orderId),
      );
      const snapshot = await getDocs(orderQuery);
      const orderItems = snapshot.docs.map((doc) => doc.data());

      // Fetch PDF template from Firebase Storage
      const templateUrl =
        "https://firebasestorage.googleapis.com/v0/b/sleepy-9b23f.firebasestorage.app/o/templates%2FF-501-019-A%20%20Borrow%20Accessories%20Form.pdf?alt=media&token=df5637ed-87c1-430a-9762-514b2712e8cd";
      const existingPdfBytes = await fetch(templateUrl).then((res) =>
        res.arrayBuffer(),
      );

      // Load Thai font
      const fontUrl = "./assets/fonts/Sarabun-Medium.ttf";
      const fontBytes = await fetch(fontUrl).then((res) => res.arrayBuffer());

      // Load PDF template and embed font
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      pdfDoc.registerFontkit(fontkit);
      const customFont = await pdfDoc.embedFont(fontBytes, { subset: true });

      // Get first page
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const { height } = firstPage.getSize();

      // Fill in common fields (same for all items in the order)
      // HN
      firstPage.drawText(`${transaction.HN || "-"}`, {
        x: 180,
        y: height / 2 + 286,
        size: 10,
        font: customFont,
      });

      // Order ID
      firstPage.drawText(`${orderId || "-"}`, {
        x: 433,
        y: height / 2 + 286,
        size: 10,
        font: customFont,
      });

      // Name
      const fullName =
        `${transaction.firstname || ""} ${transaction.lastname || ""}`.trim() ||
        "-";
      firstPage.drawText(fullName, {
        x: 180,
        y: height / 2 + 264,
        size: 10,
        font: customFont,
      });

      // Mobile
      firstPage.drawText(`${transaction.mobile || "-"}`, {
        x: 435,
        y: height / 2 + 264,
        size: 10,
        font: customFont,
      });

      // Borrow Date
      firstPage.drawText(`${transaction.datetime || "-"}`, {
        x: 180,
        y: height / 2 + 243,
        size: 10,
        font: customFont,
      });

      // Due Date
      firstPage.drawText(`${transaction.dueDateFormatted || "-"}`, {
        x: 435,
        y: height / 2 + 243,
        size: 10,
        font: customFont,
      });

      // Activity Type (Borrow/Rent)
      firstPage.drawText(`${transaction.activity || "-"}`, {
        x: 180,
        y: height / 2 + 222,
        size: 10,
        font: customFont,
      });

      // Total Price (sum of all items for Rent)
      const totalPrice = orderItems.reduce((sum, item) => {
        return sum + (item.activity === "Rent" && item.price ? item.price : 0);
      }, 0);
      const priceText = totalPrice > 0 ? `${totalPrice.toLocaleString()}` : "-";
      firstPage.drawText(priceText, {
        x: 435,
        y: height / 2 + 222,
        size: 10,
        font: customFont,
      });

      // Fill item rows with consistent spacing
      let itemY = height / 2 + 180;
      const rowSpacing = 20;

      for (const item of orderItems) {
        // Mask Name
        firstPage.drawText(`${item.maskName || "-"}`, {
          x: 180,
          y: itemY,
          size: 10,
          font: customFont,
        });

        // Mask Size
        firstPage.drawText(`Size ${item.size || "-"}`, {
          x: 310,
          y: itemY,
          size: 10,
          font: customFont,
        });

        // Quantity
        firstPage.drawText(`${item.amount || "-"}`, {
          x: 400,
          y: itemY,
          size: 10,
          font: customFont,
        });

        itemY -= rowSpacing; // Move down for next row
      }

      // Signature Name
      firstPage.drawText(fullName, {
        x: 100,
        y: 155,
        size: 10,
        font: customFont,
      });

      // Save and open PDF
      const pdfUrl = URL.createObjectURL(
        new Blob([await pdfDoc.save()], { type: "application/pdf" }),
      );
      window.open(pdfUrl, "_blank");
    } catch (error) {
      console.error("Error generating PDF:", error);
      notifyError("Error generating PDF");
    }
  };

  return (
    <React.Fragment>
      <Toast />
      <PageTitle activeMenu="Mask-Transaction" motherMenu="CPAP" />

      <div className="form-head d-flex mb-3 mb-lg-5 align-items-start flex-wrap">
        <div
          className="input-group search-area d-inline-flex"
          style={{ maxWidth: "300px" }}
        >
          <input
            type="text"
            className="form-control"
            placeholder={`Search by ${searchBy}`}
            onChange={onSearchChange}
          />
          <div className="input-group-append">
            <span className="input-group-text c-pointer">
              <i className="flaticon-381-search-2"></i>
            </span>
          </div>
        </div>

        <Dropdown className="ml-3" onSelect={(key) => setCenterFilter(key)}>
          <Dropdown.Toggle variant="outline-primary" id="dropdown-center">
            Center: {centerFilter}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {nodeOptions.map((node) => (
              <Dropdown.Item key={node} eventKey={node}>
                {node}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown className="ml-3" onSelect={(key) => setSearchBy(key)}>
          <Dropdown.Toggle variant="outline-primary" id="dropdown-searchby">
            Search by: {searchBy}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item eventKey="HN">HN</Dropdown.Item>
            <Dropdown.Item eventKey="Name">Name</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown className="ml-2" onSelect={(key) => setActivityFilter(key)}>
          <Dropdown.Toggle variant="outline-primary" id="dropdown-activity">
            Activity: {activityFilter}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item eventKey="ทั้งหมด">ทั้งหมด</Dropdown.Item>
            <Dropdown.Item eventKey="Borrow">Borrow</Dropdown.Item>
            <Dropdown.Item eventKey="Rent">Rent</Dropdown.Item>
            <Dropdown.Item eventKey="Return">Return</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown className="ml-2" onSelect={(key) => setStatusFilter(key)}>
          <Dropdown.Toggle variant="outline-primary" id="dropdown-status">
            Status: {statusFilter}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item eventKey="ทั้งหมด">ทั้งหมด</Dropdown.Item>
            <Dropdown.Item eventKey="Active">Active</Dropdown.Item>
            <Dropdown.Item eventKey="Returned">Returned</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">
                Mask Trial Order{" "}
                <Badge bg="secondary">{transactions.length}</Badge>
              </h4>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : (
                <MaskTransactionItem
                  transactions={transactions}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onDownload={handleDownloadPDF}
                  onReturn={handleReturn}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <MaskTransactionModal
        show={showModal}
        onHide={() => setShowModal(false)}
        transaction={selectedTransaction}
        onSubmit={handleEditSubmit}
      />
    </React.Fragment>
  );
};

export default MaskTransactionList;
